import {
  AudioMadeMove,
  Commercial,
  MarketSpecification,
  Role,
  Usage,
  UsageCycleNumber,
  UsageCycleUnit,
  UsagePlatform,
  UsageType,
  UsageTypeCategory,
  UsageUnitType,
  UsesType,
} from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { USD } from '@talon/web/src/utils/currency';
import { getUsageCategories, getUsageInputs, getUsagePlatforms, getUsageUsesTypes } from '@talon/web/src/utils/usages';
import { Box, BoxProps, CheckBox, Select, SelectMultiple, Text } from 'grommet';
import { Tag, User } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { LineItemDelete, TalonInput, TalonTip } from '../..';

interface IUsageRowProps {
  usage: Usage;
  commercial: Commercial;
  roles: Role[];
  checked: boolean;
  onChecked: () => void;
  onDelete: () => void;
  updateUsage: (update: Partial<Usage>) => Promise<void>;
  columnWidths?: BoxProps['width'][];
  rowIndex?: number;
}

export const UsageRow = observer(
  ({ usage, commercial, roles, checked, onChecked, onDelete, updateUsage, rowIndex = 0 }: IUsageRowProps) => {
    /** Context **/
    const estimateStore = useEstimateStore();

    /** State **/
    const [wages, setWages] = useState(0);
    const [total, setTotal] = useState(0);
    const [usageMarkets, setUsageMarkets] = useState<MarketSpecification[]>();

    /** Computed **/
    const usageEstimateItems = estimateStore.getTempUsageEstimateItems(commercial.id, usage.id);

    const {
      cycleLabel: usageCycleType,
      cycleTypes: usageCycleTypes,
      matchingTypeInfos,
      isRadioNetworkPerUse,
      showCycles: showCyclesInput,
      showUses: showUsesInput,
    } = useMemo(() => getUsageInputs(usage, commercial), [commercial, usage]);
    const usagePlatforms = useMemo(() => getUsagePlatforms(commercial.type), [commercial]);
    const usageCategories = useMemo(() => getUsageCategories(commercial.type, usage.platform), [commercial, usage]);
    const usageUsesTypes = useMemo(
      () => getUsageUsesTypes(commercial.type, usage.platform, usage.category),
      [commercial, usage]
    );

    const showMarketSelect = useMemo(
      () =>
        matchingTypeInfos &&
        matchingTypeInfos.length > 0 &&
        matchingTypeInfos.length <= Object.values(MarketSpecification).length &&
        matchingTypeInfos.findIndex((info) => !!info.marketSpec) > -1,
      [matchingTypeInfos]
    );

    const showAudioMadeMoveSelect = useMemo(
      () =>
        matchingTypeInfos &&
        matchingTypeInfos.length <= 2 &&
        matchingTypeInfos.length > 0 &&
        (!!matchingTypeInfos[0]?.audioMadeMove || !!matchingTypeInfos[1]?.audioMadeMove),
      [matchingTypeInfos]
    );

    const showTypeSelect =
      (usage.uses_type === UsesType.Unlimited &&
        (!usageCycleTypes || !usageCycleTypes.length) &&
        !!matchingTypeInfos?.length) ||
      isRadioNetworkPerUse;

    /** Methods **/

    const handleChangePlatform = async (platform: UsagePlatform) => {
      const newUsageCategories = getUsageCategories(commercial.type, platform);
      await updateUsage({
        platform,
        category: newUsageCategories?.length === 1 ? newUsageCategories[0] : null,
        cycles: 1,
        cycle_number: null,
        cycle_unit: null,
        units: 1,
        unit_type: UsageUnitType.Uses,
        uses_type: null,
        language: null,
        market_spec: null,
        global_market: null,
        cable_market: null,
        audio_made_move: null,
        type: null,
      });
    };

    const handleChangeCategory = async (category: UsageTypeCategory) => {
      await updateUsage({
        category,
        cycles: 1,
        cycle_number: null,
        cycle_unit: null,
        units: 1,
        unit_type: UsageUnitType.Uses,
        uses_type: null,
        language: null,
        market_spec: null,
        global_market: null,
        cable_market: null,
        audio_made_move: null,
        type: showTypeSelect ? null : undefined,
      });
    };

    const handleChangeUsesType = async (usesType: UsesType) => {
      await updateUsage({
        uses_type: usesType,
        units: 1,
        cycles: 1,
        cycle_number: null,
        cycle_unit: null,
        type: showTypeSelect ? null : undefined,
      });
    };

    const handleChangeCycle = async (cycleLabel: string) => {
      const [cycle_number, cycle_unit] = cycleLabel.split(' ') as [UsageCycleNumber, UsageCycleUnit];
      await updateUsage({
        cycle_number,
        cycle_unit,
        type: showTypeSelect ? null : undefined,
        audio_made_move: null,
        market_spec: null,
      });
    };

    const handleChangeAudioMadeMove = async (audioMadeMove: AudioMadeMove) => {
      await updateUsage({ audio_made_move: audioMadeMove, type: null });
    };

    const handleChangeMarket = async (market: MarketSpecification) => {
      await updateUsage({ market_spec: market, type: null });
    };

    const handleChangeType = async (type: UsageType | null) => {
      await updateUsage({ type });
    };

    const handleChangeUses = async (unitCount: string) => {
      const units = parseInt(unitCount);
      if (units > 0) await updateUsage({ units });
    };

    const handleChangeCycles = async (cycleCount: string) => {
      const cycles = parseInt(cycleCount);
      if (cycles > 0) await updateUsage({ cycles });
    };

    /** Effects **/

    useEffect(() => {
      if (usageEstimateItems) {
        let newWages = 0;
        let newTotal = 0;
        if (usage.type) {
          for (const item of usageEstimateItems) {
            newWages += item.subtotal;
            newTotal += item.subtotal_plus_fringe;
          }
        }
        if (newWages !== wages) setWages(newWages);
        if (newTotal !== total) setTotal(newTotal);
      }
    }, [usage, usageEstimateItems]);

    useEffect(() => {
      if (matchingTypeInfos?.length > 1) {
        const markets: MarketSpecification[] = [];
        for (const info of matchingTypeInfos) {
          if (info.marketSpec) markets.push(info.marketSpec);
        }
        setUsageMarkets(markets);
      }
    }, [matchingTypeInfos]);

    /** Render **/
    return (
      <Box
        direction="row"
        gap="small"
        align="center"
        pad="small"
        border={rowIndex > 0 && { side: 'top', color: 'light-3' }}
        className="line-item"
      >
        <Box width="48px" height="48px" align="center" justify="center">
          <CheckBox onChange={onChecked} checked={checked} />
        </Box>
        <Box width="75%" direction="row" align="center" gap="small">
          <Box width="14rem">
            <SelectMultiple
              options={roles ?? []}
              value={usage.roles?.reduce((acc, curr) => (curr.role_group ? acc : acc.concat(curr)), [] as Role[])}
              valueKey="id"
              labelKey="name"
              placeholder="Choose roles..."
              size="medium"
              onChange={({ value }) => updateUsage({ roles: value })}
              showSelectedInline
              icon={
                <TalonTip content={<Text>Choose Roles</Text>}>
                  <User />
                </TalonTip>
              }
              valueLabel={(values: Role[]) => (
                <Box wrap justify="center" pad={{ vertical: 'xxsmall' }}>
                  {values && values.length ? (
                    <>
                      {values.map((role) => {
                        const payScalePercent =
                          role.performer?.contracts[role.performer.contracts.length - 1].compensation[0].percentOfScale;
                        return (
                          <Box
                            key={role.id}
                            direction="row"
                            align="center"
                            justify="between"
                            pad={{ horizontal: 'small' }}
                          >
                            <Text size="small">{role.name}</Text>
                            {(payScalePercent ?? 0) > 100 && (
                              <TalonTip content={<Text>{`Performer receives ${payScalePercent}% of scale.`}</Text>}>
                                <Tag size="14" />
                              </TalonTip>
                            )}
                          </Box>
                        );
                      })}
                    </>
                  ) : (
                    <Text color="text-weak">Select Roles</Text>
                  )}
                </Box>
              )}
            />
          </Box>

          {/**
           * Platform
           */}
          <Box width="13rem">
            {usagePlatforms && (
              <Select
                options={usagePlatforms}
                size="small"
                value={usage.platform ?? ''}
                onChange={({ value }) => handleChangePlatform(value)}
                placeholder="Select Platform"
              />
            )}
          </Box>

          {/**
           * Category
           */}
          <Box width="15rem">
            {usage.platform && (
              <Select
                options={usageCategories ?? []}
                size="small"
                value={usage.category ?? ''}
                onChange={({ value }) => handleChangeCategory(value)}
                placeholder="Select Category"
              />
            )}
          </Box>

          {/**
           * Uses Type
           */}
          <Box width="13%">
            {usage.platform && usage.category && (
              <Select
                options={usageUsesTypes ?? []}
                value={usage.uses_type ?? ''}
                size="small"
                onChange={({ value }) => {
                  handleChangeUsesType(value);
                }}
                placeholder="Select Uses"
              />
            )}
          </Box>

          {/**
           * Cycle Type
           */}
          {usage.uses_type && usageCycleTypes && (
            <Box width="8.5rem">
              <Box direction="row" align="center" gap="xsmall">
                <Select
                  options={usageCycleTypes ?? []}
                  value={usageCycleType ?? ''}
                  size="small"
                  onChange={({ value }) => handleChangeCycle(value)}
                  placeholder="Select Cycle"
                />
              </Box>
            </Box>
          )}

          {/**
           * Usage Type
           */}
          {showTypeSelect && (
            <Box width="15%" direction="row" align="center" gap="xsmall">
              <Select
                options={matchingTypeInfos?.map((info) => ({ label: info.label, value: info.id })) ?? []}
                labelKey="label"
                valueKey="value"
                value={usage.type ?? ''}
                size="small"
                onChange={({ value }) => handleChangeType(value.value)}
                placeholder="Select Type"
              />
            </Box>
          )}

          {/**
           * Audio Made For/Move Over
           */}
          {showAudioMadeMoveSelect && (
            <Box width="12rem">
              <Select
                options={Object.values(AudioMadeMove)}
                size="small"
                value={usage.audio_made_move ?? ''}
                onChange={({ value }) => handleChangeAudioMadeMove(value)}
                placeholder="Select Audio Use"
              />
            </Box>
          )}

          {/**
           * Markets
           */}
          {showMarketSelect && !!usageMarkets?.length && (
            <Box width="12rem">
              <Select
                options={usageMarkets ?? []}
                valueKey="value"
                labelKey="label"
                size="small"
                value={usage.market_spec ?? ''}
                onChange={({ value }) => handleChangeMarket(value)}
                placeholder="Select Market"
              />
            </Box>
          )}

          {/**
           * Uses
           */}
          <Box>
            {showUsesInput && (
              <Box width="8rem" direction="row" align="center" gap="small">
                <TalonInput
                  val={usage.units}
                  inputType="number"
                  minValue={1}
                  placeholder="Uses"
                  onChange={(uses) => handleChangeUses(uses)}
                />
                <Text>Uses</Text>
              </Box>
            )}

            {/**
             * Cycles
             */}
            {showCyclesInput && (
              <Box width="9rem" direction="row" align="center" gap="small">
                <TalonInput
                  val={usage.cycles}
                  inputType="number"
                  minValue={1}
                  placeholder="Cycles"
                  onChange={(cycles) => handleChangeCycles(cycles)}
                />
                <Text>Cycles</Text>
              </Box>
            )}
          </Box>
        </Box>

        <Box flex />

        <Box direction="row" justify="end" pad={{ vertical: 'xsmall' }} gap="large">
          <Box direction="row" gap="xsmall" justify="end" align="center">
            <Text size="xsmall" color="text-label">
              Wages
            </Text>
            {<Text weight={500}>{USD(wages)}</Text>}
          </Box>
          <Box direction="row" gap="xsmall" justify="end" align="center" width="10rem">
            <Text size="xsmall" color="text-label">
              Total
            </Text>
            {<Text weight={500}>{USD(total)}</Text>}
          </Box>
        </Box>
        <LineItemDelete name={usage.title || 'usage'} onClick={onDelete} />
      </Box>
    );
  }
);
